/**
 * Clinical Dashboard Service
 *
 * Provides API client methods for clinical quality metrics:
 * - ROS completeness per patient (14 organ systems)
 * - SOAP completion percentage per consultation (>80% target)
 * - Patients not seen in 90+ days
 *
 * All endpoints are read-only (no data modification).
 */

import apiClient from './apiClient';

/**
 * ROS completeness data for a patient
 */
export interface RosCompleteness {
  documented: number;
  total: number;
  percentage: number;
  is_complete: boolean;
}

/**
 * ROS per-system breakdown
 */
export interface RosBreakdown {
  systems: Array<{
    code: string;
    label: string;
    documented: boolean;
  }>;
  documented_count: number;
  total: number;
  percentage: number;
}

/**
 * SOAP completion data for a consultation
 */
export interface SoapCompletion {
  percentage: number;
  sections: {
    subjective: { filled: boolean; label: string };
    objective: { filled: boolean; label: string };
    assessment: { filled: boolean; label: string };
    plan: { filled: boolean; label: string };
  };
  completed_count: number;
  total_sections: number;
  status: 'complete' | 'incomplete' | 'critical';
  highlight: boolean;
  target_met: boolean;
}

/**
 * Inactive patient data
 */
export interface InactivePatient {
  id: number;
  first_name: string;
  last_name: string;
  medical_record_number: string;
  days_since_last_visit: number | null;
  last_consultation_date: string | null;
}

/**
 * Dashboard summary metrics
 */
export interface DashboardSummary {
  period: {
    from: string;
    to: string;
  };
  soap_completion: {
    average_percentage: number;
    target_met: boolean;
    consultations_analyzed: number;
  };
  ros_coverage: {
    consultations_with_ros: number;
    total_consultations: number;
  };
  inactive_patients: {
    count: number;
    threshold_days: number;
  };
}

/**
 * Inactive patients list response
 */
export interface InactivePatientsResponse {
  patients: InactivePatient[];
  count: number;
  threshold_days: number;
}

/**
 * Clinical Dashboard API service
 */
export const clinicalDashboardService = {
  /**
   * GET /api/v1/clinical/dashboard
   *
   * Returns clinical quality metrics summary for the authenticated doctor,
   * or org-wide metrics for admin users.
   *
   * @param doctorId Optional doctor ID filter (admin only)
   */
  async getDashboardSummary(doctorId?: number): Promise<DashboardSummary> {
    const params: Record<string, number> = {};
    if (doctorId !== undefined) {
      params.doctor_id = doctorId;
    }
    const response = await apiClient.get<{ data: DashboardSummary }>('/clinical/dashboard', { params });
    return (response.data.data ?? response.data) as DashboardSummary;
  },

  /**
   * GET /api/v1/clinical/patients/{patientId}/ros
   *
   * Returns ROS completeness breakdown for a specific patient.
   */
  async getPatientRos(patientId: number): Promise<{ patient_id: number; ros: RosBreakdown }> {
    const response = await apiClient.get<{ data: { patient_id: number; ros: RosBreakdown } }>(
      `/clinical/patients/${patientId}/ros`
    );
    return (response.data.data ?? response.data) as { patient_id: number; ros: RosBreakdown };
  },

  /**
   * GET /api/v1/clinical/patients/{patientId}/inactive-status
   *
   * Returns inactive status for a specific patient.
   */
  async getPatientInactiveStatus(
    patientId: number
  ): Promise<{
    patient_id: number;
    has_visits: boolean;
    days_since_last_visit: number | null;
    is_inactive: boolean;
    inactive_threshold_days: number;
  }> {
    const response = await apiClient.get<{
      data: {
        patient_id: number;
        has_visits: boolean;
        days_since_last_visit: number | null;
        is_inactive: boolean;
        inactive_threshold_days: number;
      };
    }>(`/clinical/patients/${patientId}/inactive-status`);
    return (response.data.data ?? response.data) as {
      patient_id: number;
      has_visits: boolean;
      days_since_last_visit: number | null;
      is_inactive: boolean;
      inactive_threshold_days: number;
    };
  },

  /**
   * GET /api/v1/clinical/inactive-patients
   *
   * Returns list of patients not seen in 90+ days.
   * Admin sees all; doctors see only their own.
   *
   * @param doctorId Optional doctor ID filter (admin only)
   */
  async getInactivePatients(doctorId?: number): Promise<InactivePatientsResponse> {
    const params: Record<string, number> = {};
    if (doctorId !== undefined) {
      params.doctor_id = doctorId;
    }
    const response = await apiClient.get<{ data: InactivePatientsResponse }>('/clinical/inactive-patients', {
      params,
    });
    return (response.data.data ?? response.data) as InactivePatientsResponse;
  },
};